import formatter from '../utils/formatter';
import { IQueryOptions, IExecutionOptions, IQueryOptionsSort, IQueryContext, TQueryType, DebugType, IQueryContextExternalApiResponse, IQueryContextExternalApiRequest } from '../models/query';
import { get } from '../utils/get';
import { isNullOrUndefined } from '../utils/typeChecks';
import { IStatistics } from '../models/api/search';
import { IFundManager, IContentItem } from '../models/api/dictionary';
import { Transform } from './transform/Transform';
import { Query } from './Query';
import { TranslationHelper } from '../helpers/TranslationHelper';
import { isArrayLike } from '../utils/isArrayLike';
import { IScenario, IPortfolio, IPortfolioEntity, IScenarioReport } from '../models/portal';
import { IFormat } from '../models/data';
import { deepMergeObjects } from '../utils/object';
import { IUser } from '../models/authentication';
import { IQueryOptionsExternalApi } from '../models/query/options/externalApi/IQueryOptionsExternalApi';
import { replacePlaceholders } from '../utils';
import { AppParamsHelper } from '../helpers';

export const MAX_CALL_STACK_LEVEL: number = 50;

type TChildQueries<T> = {
	[K in keyof T]: T[K] extends IQueryOptions ? K : never;
}[keyof T];

type TNotChildQueries<T> = {
	[K in keyof T]: T[K] extends IQueryOptions ? never : K;
}[keyof T];

export abstract class BaseQuery<TChildOptions = any> {
	private debugBase(type: DebugType, ...messages: any[]) {
		if (this.queryOptions && this.queryOptions.debug && this.queryOptions.debug.key) {
			const prefix = `QueryOptions Debug - "${this.queryOptions.debug.key}": `;
			console[type](prefix, ...messages);
		}
	}
	debug = {
		error: (...messages: any[]) => {
			this.debugBase(DebugType.error, ...messages);
		},
		info: (...messages: any[]) => {
			this.debugBase(DebugType.info, ...messages);
		},
		warn: (...messages: any[]) => {
			this.debugBase(DebugType.warn, ...messages);
		},
	};
	constructor(public queryOptions: IQueryOptions, public translationHelper?: TranslationHelper) { }
	abstract getRawValue<T = any>(options: IExecutionOptions, callStackLevel?: number): T;
	abstract getOptions(): TChildOptions;
	rawOptions: TChildOptions;
	get options(): TChildOptions {
		if (!this.rawOptions) {
			this.rawOptions = this.getOptions();
		}
		return this.rawOptions;
	}
	fillKeyFromQuery(fillKey: TNotChildQueries<TChildOptions>, queryKey: TChildQueries<TChildOptions>, executionOptions: IExecutionOptions) {
		const childQueryOptions = (this.options && this.options[queryKey]) as any as IQueryOptions;
		if (this.options && childQueryOptions) {
			const query = new Query(childQueryOptions, this.translationHelper);
			this.options[fillKey] = query.execute(executionOptions);
		}
	}
	initialize(executionOptions: IExecutionOptions) {
		// Used to override
	}
	execute<T = any>(executionOptions: IExecutionOptions, callStackLevel: number = 1): T {
		if (callStackLevel > MAX_CALL_STACK_LEVEL) {
			console.warn('BaseQuery MAX_CALL_STACK_LEVEL exceeded');
			return;
		}
		this.debug.info('Initialize started');
		this.initialize(executionOptions);
		const { culture, placeholder: defaultPlaceholder, translate } = executionOptions;
		const { format, placeholder = defaultPlaceholder, performanceCheckPlaceholder, transforms, sort } = this.queryOptions;
		// Validate that the performance values are allowed and this is a performance value
		if (!this.passesPerformanceCheck(executionOptions)) {
			this.debug.info('Performance check failed');
			let performanceCheckPlaceholderValue;
			if (performanceCheckPlaceholder) {
				const placeHolderQuery = new Query(performanceCheckPlaceholder);
				performanceCheckPlaceholderValue = placeHolderQuery.execute(executionOptions);
			}
			return performanceCheckPlaceholderValue || placeholder as any;
		}
		let rawValue = this.getRawValue(executionOptions, callStackLevel);
		this.debug.info('getRawValue = ', rawValue);
		if (isNullOrUndefined(rawValue)) {
			rawValue = placeholder;
			this.debug.info('rawValue set to placeholder');
		}

		if (transforms) {
			this.debug.info('has transforms');
			for (const transformOptions of transforms) {
				this.debug.info(`Starting transform of type: "${transformOptions.transformOptionsType}"`, { transformOptions });
				this.debug.info(`rawValue before transform`, { rawValue });
				const transform = new Transform(transformOptions, this.translationHelper, callStackLevel);
				rawValue = transform.execute(rawValue, this.queryOptions, executionOptions);
				this.debug.info(`rawValue after transform`, { rawValue });
			}
		}

		if (format) {
			this.debug.info('has format');
			this.debug.info(`rawValue before format`, { rawValue });
			rawValue = this.applyFormat(format, rawValue, executionOptions);
			this.debug.info(`rawValue after format`, { rawValue });
		}

		if (translate && typeof rawValue === 'string') {
			this.debug.info('has translate');
			this.debug.info(`rawValue before translate`, { rawValue });
			rawValue = translate(rawValue) as any as T;
			this.debug.info(`rawValue after translate`, { rawValue });
			this.debug.info('response', { rawValue });
			return rawValue;
		}
		if (Array.isArray(rawValue) && sort) {
			this.debug.info('has sort');
			this.debug.info(`rawValue before sort`, { rawValue });
			rawValue = this.applySort(rawValue, sort, culture, executionOptions.inputs);
			this.debug.info(`rawValue after sort`, { rawValue });
		}
		this.debug.info('response', { rawValue });
		return rawValue;
	}
	applyFormat(format: IFormat, rawValue: any, executionOptions: IExecutionOptions) {
		const { currency, culture, translate } = executionOptions;
		if (format.globalFormatKey) {
			const globalFormat = executionOptions && executionOptions.globalFormatsByKey && executionOptions.globalFormatsByKey[format.globalFormatKey];
			if (globalFormat && globalFormat.format) {
				format = deepMergeObjects(globalFormat.format, format);
			}
		}
		const { type, transforms, options, optionsByCulture = {} } = format;
		const optionByCulture = optionsByCulture[culture];
		const { subQueries = [] } = this.queryOptions;
		let workingCurrency = currency;
		if (subQueries) {
			const currencyQueryOptions = subQueries.find(subQuery => subQuery.queryId === 'currency');
			if (currencyQueryOptions) {
				const currencyQuery = new Query(currencyQueryOptions);
				workingCurrency = currencyQuery.execute(executionOptions);
			}
		}
		return formatter(type, culture, workingCurrency, optionByCulture || options, rawValue, transforms, translate);
	}
	passesPerformanceCheck(executionOptions: IExecutionOptions): boolean {
		const context = Query.getContext(executionOptions, this.queryOptions);
		const { isPerformanceAllowed = false } = (context || {});
		const { checkPerformanceAllowed } = this.queryOptions;
		return (!checkPerformanceAllowed || isPerformanceAllowed);
	}
	applySort(collection: any[], sorts: IQueryOptionsSort | IQueryOptionsSort[], culture?: string, inputs?: object) {
		if (sorts) {
			const orders = {};
			if (!Array.isArray(sorts) || !isArrayLike(sorts)) {
				sorts = [sorts as IQueryOptionsSort];
			}
			sorts.forEach((sort, sortIndex) => {
				if (sort.order && sort.order.length > 0) {
					let order = sort.translateOrderValues && this.translationHelper && this.translationHelper.translate ?
						sort.order.map(value => this.translationHelper.translate(value)) :
						sort.order;
					if (inputs) {
						/**
						 * Due to the replacePlaceholders expecting the AppParamsHelper to get the embed values
						 * and it not being available to the Queries as the app initialize the AppParamsHelper
						 * and populates the executionOptions.inputs based on that initialization,
						 * we need to create a mock AppParamsHelper with the values on the rawAppParams
						 * for use by the replace placeholders.
						 * This will maintain consistency between Fund Center and the apps.
						 * We could not update replacePlaceholders to not use the appParamsHelper as we are not
						 * sure if there are any other application that might be using this.
						 */
						const appParams: AppParamsHelper = { rawAppParams: inputs } as any;
						order = order.map(value => replacePlaceholders({ text: value, appParamsHelper: appParams }));
					}
					const key = `sort_${sortIndex}`;
					orders[key] = order;
					sort['_sortKey'] = key;
				}
			});

			/*
				collection = collection
						.map((value) => {
							const orderIndex = order.indexOf(value[sort.key]);
							value.__order =
								orderIndex > -1 ? orderIndex : order.length;
							return value;
						})
						.sort((a, b) => this.sorters.number(a.__order, b.__order))
						.map((value) => {
							delete value.__order;
							return value;
						});
			*/
			const getOrderValue = (sort: IQueryOptionsSort, value: any) => {
				const order = orders[sort['_sortKey']];
				const orderIndex = order.indexOf(value[sort.key]);
				return orderIndex > -1 ? orderIndex : order.length;
			};
			const innerSort = (a: any, b: any, sort: IQueryOptionsSort) => {
				if (sort.order && sort['_sortKey']) {
					const aOrder = getOrderValue(sort, a);
					const bOrder = getOrderValue(sort, b);
					return this.sorters.number(aOrder, bOrder);
				}
				const sortFunction: (a: any, b: any, culture?: string) => number = this.sorters[sort.type];
				if (sortFunction) {
					const aValue = get(a, sort.key);
					const bValue = get(b, sort.key);
					return sortFunction(aValue, bValue, culture);
				}
				return 0;
			};

			collection.sort((a, b) => {
				for (const sort of (sorts as IQueryOptionsSort[])) {
					let result = innerSort(a, b, sort);
					if (result !== 0) {
						if (sort.direction === 'DESC') {
							result = result * -1;
						}
						return result;
					}
				}
				return 0;
			});
			sorts.forEach((sort) => {
				delete sort['_sortKey'];
			});
		}
		return collection;
	}

	getScenarioFromContext(executionOptions: IExecutionOptions): IScenario | undefined {
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.scenario;
		}
		return;
	}

	getScenarioReportFromContext(executionOptions: IExecutionOptions): IScenarioReport | undefined {
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.scenarioReport;
		}
		return;
	}

	getPortfolioFromContext(executionOptions: IExecutionOptions): IPortfolio | undefined {
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.portfolio;
		}
		return;
	}

	getPortfolioEntityFromContext(executionOptions: IExecutionOptions): IPortfolioEntity | undefined {
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.portfolioEntity;
		}
		return;
	}

	getFundManager(executionOptions: IExecutionOptions): IFundManager | undefined {
		const { fundManagerContext } = executionOptions;
		if (fundManagerContext) {
			return fundManagerContext;
		}
		return;
	}

	getLoggedInUser(executionOptions: IExecutionOptions): IUser | undefined {
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context && context.loggedInUser) {
			return context.loggedInUser;
		}
		return;
	}

	getStatistic(executionOptions: IExecutionOptions): IStatistics | undefined {
		const { statisticContext } = executionOptions;
		if (statisticContext) {
			return statisticContext;
		}
		return;
	}
	sorters = {
		date: (a: string | number, b: string | number) => {
			const aDate = new Date(a);
			const bDate = new Date(b);
			return Number(aDate) - Number(bDate);
		},
		number: (a: number, b: number) => {
			return a - b;
		},
		string: (a: string, b: string, culture) => {
			const aString = a.toString().toUpperCase();
			const bString = b.toString().toUpperCase();
			return aString.localeCompare(bString, culture);
		},
	};

	getExternalApiResponse(externalApiOptions: IQueryOptionsExternalApi, executionOptions: IExecutionOptions): IQueryContextExternalApiResponse | undefined {
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context) {
			const { externalApiResponses } = context;
			if (externalApiResponses && externalApiOptions) {
				const { id } = externalApiOptions;
				return externalApiResponses[id];
			}
		}
		return;
	}

	getExternalApiRequest(externalApiOptions: IQueryOptionsExternalApi, executionOptions: IExecutionOptions): IQueryContextExternalApiRequest | undefined {
		const contextConfiguration = Query.getContextConfiguration(executionOptions, this.queryOptions);
		if (contextConfiguration) {
			const { id } = externalApiOptions;
			const { externalApiRequests = [] } = contextConfiguration;
			return externalApiRequests.find(externalApiRequest => externalApiRequest.id === id);
		}
		return;
	}
}